/**
 * Add button rendered inside a completed `suggest_custom_field` action card. The server reports
 * the stored insight id as `insightId` on the action; without one nothing is rendered.
 *
 * @module apps/web/src/components/flux-agent/learning/FieldSuggestionAddButton
 */

import { Check, Loader2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { FluxAgentAction } from '@/lib/fluxAgentApi';
import { useApplySuggestion } from './useApplySuggestion';

/** Inputs for {@link FieldSuggestionAddButton}. */
export interface FieldSuggestionAddButtonProps {
  /** The field-suggestion action card's action. */
  action: FluxAgentAction;
}

/** Offer one-tap Add for the field a `suggest_custom_field` call stored. */
export function FieldSuggestionAddButton({ action }: FieldSuggestionAddButtonProps) {
  const { outcomes, add } = useApplySuggestion();
  const insightId = (action as FluxAgentAction & { insightId?: string }).insightId;
  if (!insightId || action.status !== 'completed') return null;
  const outcome = outcomes[insightId];
  const name = action.title.replace(/^Suggested a field:\s*/, '');
  if (outcome === 'added')
    return (
      <p className="text-primary mt-1 flex items-center gap-1 text-xs font-medium" role="status">
        <Check className="h-3.5 w-3.5" /> Added to your fields
      </p>
    );
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      className="mt-1.5 h-7 gap-1"
      disabled={outcome === 'pending'}
      onClick={() => void add(insightId, name)}
      aria-label={`Add field ${name}`}
    >
      {outcome === 'pending' ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <Plus className="h-3.5 w-3.5" />
      )}
      Add field
    </Button>
  );
}
